<script lang="ts" setup>
import { useRoute } from 'vue-router';
import { onKeyStroke, useLocalStorage } from '@vueuse/core';
import { ToastProvider, TooltipProvider } from 'reka-ui';
import { useToast } from '@/components/toast/useToast';
import { useI18n } from '@/i18n/useI18n';
import { useStore } from '@/store/useStore';
import PublicFeatureFlagsMenu from '@/components/debug/PublicFeatureFlagsMenu.vue';
import ToastViewport from '@/components/toast/ToastViewport.vue';
import {
  useThemePreference,
  useCurrentUser,
  useCurrentUserPermissions,
  type ThemePreference,
} from '@/modules/current-user/composables';
import { useCurrentOrg } from '@/modules/current-org/composables';
import NoPermissions from '@/modules/errors/NoPermissions.vue';
import { setUserProperties } from '@/libs/analytics';
import { useCurrentOrgRoles } from '@/modules/roles/composables';
import useHasPermissions from '@/composables/permissions';
import type Permission from '@/composables/permissions/permissions';
import { useTrial } from '@/modules/trial/composables';

const { t } = useI18n();
const toast = useToast();

const store = useStore();
const { user } = useCurrentUser();
const { currentOrg } = useCurrentOrg();
const { trial } = useTrial();

watchEffect(() => {
  if (!user.value || !currentOrg.value) return;
  setUserProperties({
    status: store.state.assessment.status,
    user: user.value,
    org: currentOrg.value,
    trial: trial.value || undefined,
  });
});

// For persisting debug user role, we watch it from App.vue
// userRole is set from DebugMenuContent.vue
const { roles } = useCurrentOrgRoles();
const userRole = useLocalStorage<string | undefined>('debug-user-role', undefined);
const { override } = useCurrentUserPermissions();

watchEffect(() => {
  if (userRole.value) {
    const role = roles.value?.find((r) => r.id === userRole.value);
    if (role) {
      override(role.permissions);
    }
  }
});

const showPublicFlags = computed(
  () => import.meta.env.VUE_APP_PUBLIC_FLAGS_TOOL === 'true' && !user.value,
);

watch(
  () => store.state.legacy.api.api_error,
  (error) => {
    if (!error) return;
    // We don't want to show the toast for the tags error - it's handled inline
    if (error !== 'API_ERROR_TAGS') {
      toast.error(t(`errors.${error}`));
    }
    nextTick(() => {
      store.dispatch('legacy/clearGlobalError');
    });
  },
);

const {
  isReady,
  hasAnyOfPermissions,
  hasClientPermissions,
  hasSupplierPermissions,
  hasFederatedPermissions,
} = useHasPermissions();

const route = useRoute();
const canAccess = computed(() => {
  if (!isReady.value) return true;

  // We do it like this so that parent routes get merged with children
  const permissions: Permission[] = [];
  let mode = '';
  for (const match of route.matched) {
    if (Array.isArray(match.meta.permissions)) {
      permissions.push(...match.meta.permissions);
    }
    if (typeof match.meta.mode === 'string') mode = match.meta.mode;
  }

  if (
    (mode === 'client' && !hasClientPermissions.value) ||
    (mode === 'supplier' && !hasSupplierPermissions.value) ||
    (mode === 'federated' && !hasFederatedPermissions.value)
  )
    return false;

  if (!permissions.length) return true;

  return hasAnyOfPermissions(...(permissions as Permission[]));
});

const isFontLoaded = ref(false);
provide('isFontLoaded', isFontLoaded);
onMounted(async () => {
  const FONT_STRING = '14px "Material Symbols Outlined"';
  await document.fonts.load(FONT_STRING);
  // We have observed an issue in Chrome 140.0.7339.208 where this value was sometimes set to false, even when the font was loaded
  isFontLoaded.value = document.fonts.check(FONT_STRING);
});

const { theme, themePreference } = useThemePreference();
watchEffect(() => {
  // This must be set on the body, not just #app or a div further down, so that
  // it also applies to teleported stuff like modals
  document.body.classList.toggle('tw-dark', theme.value === 'dark');

  window.Intercom('update', {
    theme_mode: theme.value === 'dark' ? 'dark' : 'light',
  });
});

// This is a dev tool to quickly change the theme
const currentEnv = import.meta.env.MODE;
const themes: ThemePreference[] = ['classic', 'light', 'dark'];

onKeyStroke('T', (k) => {
  if (currentEnv !== 'prod') {
    // this won't work on windows, but our devs are only using macs if you are
    // coming here to make this work on windows, feel free to curse my name.
    if (k.ctrlKey && k.shiftKey) {
      themePreference.value = themes[(themes.indexOf(themePreference.value) + 1) % themes.length];
      // eslint-disable-next-line no-console
      console.log('changing theme to:', themePreference.value);
    }
  }
});
</script>

<template>
  <div class="h-full">
    <ToastProvider disable-swipe>
      <TooltipProvider :delay-duration="200">
        <VueAnnouncer class="sr-only" />
        <PublicFeatureFlagsMenu v-if="showPublicFlags" class="fixed bottom-5x left-5x z-[9999]" />
        <RouterView v-if="canAccess" />
        <NoPermissions v-else />

        <ToastViewport />
      </TooltipProvider>
    </ToastProvider>
  </div>
</template>
